import React from 'react';
import { classNames } from '../../utils/classNames';
import { ArrowTrendingUpIcon, BanknotesIcon, ExclamationTriangleIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { Card, CardBody, CardHeader } from '../ui';

type StatVariant = 'primary' | 'success' | 'warning' | 'neutral';

const variantStyles: Record<StatVariant, { bg: string; fg: string; ring: string }> = {
  primary: {
    bg: 'bg-primary-500/10',
    fg: 'text-primary-600',
    ring: 'ring-primary-100',
  },
  success: {
    bg: 'bg-success/10',
    fg: 'text-success',
    ring: 'ring-success/20',
  },
  warning: {
    bg: 'bg-warning/10',
    fg: 'text-warning',
    ring: 'ring-warning/20',
  },
  neutral: {
    bg: 'bg-surface-subtle',
    fg: 'text-[var(--app-text-muted)]',
    ring: 'ring-[color:var(--app-border)]',
  },
};

type StatCardProps = {
  title: string;
  amount: string;
  subtitle?: string;
  delta?: string;
  variant?: StatVariant;
  icon?: React.ReactNode;
};

const StatCard: React.FC<StatCardProps> = ({
  title,
  amount,
  subtitle,
  delta,
  variant = 'primary',
  icon,
}) => {
  const styles = variantStyles[variant];

  return (
    <Card className="flex flex-col">
      <CardBody className="flex flex-1 flex-col gap-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-secondary-500">{title}</p>
            <p className="text-2xl font-semibold text-[var(--app-heading)]">{amount}</p>
            {subtitle ? <p className="text-xs text-[var(--app-text-muted)]">{subtitle}</p> : null}
          </div>
          <div
            className={classNames(
              'flex h-12 w-12 items-center justify-center rounded-2xl ring-4 transition',
              styles.bg,
              styles.fg,
              styles.ring
            )}
          >
            {icon}
          </div>
        </div>
        {delta ? (
          <div className="flex items-center gap-2 rounded-full bg-surface-subtle px-3 py-1 text-xs font-medium text-primary-600">
            {delta}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
};

type StatsCardsProps = {
  data: Array<
    Pick<StatCardProps, 'title' | 'amount' | 'subtitle' | 'delta' | 'variant'> & {
      icon?: React.ReactNode;
      key: string;
    }
  >;
};

export const StatsCards: React.FC<StatsCardsProps> = ({ data }) => {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {data.map((stat) => (
        <StatCard key={stat.key} {...stat} />
      ))}
    </div>
  );
};

export const defaultStats = [
  {
    key: 'payments_today',
    title: 'Платежи за сегодня',
    amount: '1 430 000 ₸',
    subtitle: '38 успешных / 3 отклонено',
    delta: '+12% к вчерашнему дню',
    variant: 'primary' as StatVariant,
    icon: <BanknotesIcon className="h-6 w-6" />,
  },
  {
    key: 'withdrawals_queue',
    title: 'Запросы на вывод',
    amount: '8 в очереди',
    subtitle: '3 ожидают более 30 минут',
    variant: 'warning' as StatVariant,
    icon: <ArrowTrendingUpIcon className="h-6 w-6" />,
  },
  {
    key: 'auto_matches',
    title: 'Автоматические совпадения',
    amount: '92%',
    subtitle: '23 из 25 платежей сегодня',
    delta: '+5% за последнюю неделю',
    variant: 'success' as StatVariant,
    icon: <UserGroupIcon className="h-6 w-6" />,
  },
  {
    key: 'alerts',
    title: 'Требует внимания',
    amount: '2 инцидента',
    subtitle: 'Проверьте ошибки по платежам',
    variant: 'neutral' as StatVariant,
    icon: <ExclamationTriangleIcon className="h-6 w-6" />,
  },
];

export default StatsCards;

